/**
 * useVerticalSpacingOnResize - Auto-adjusts vertical spacing when node heights change
 * 
 * When card display settings change (show/hide fields), card heights can change,
 * causing overlaps. This hook detects dimension changes and pushes nodes DOWN only
 * when they would overlap horizontally overlapping stacks.
 *
 * Single-campaign with settled ELK: disabled — size changes trigger a debounced ELK
 * relayout instead (`useElkRelayoutOnNodeDimensionsChange`).
 * 
 * KEY INSIGHT: Nodes that are side-by-side (different X positions) don't need
 * spacing adjustments even if they're at the same Y level.
 */

import { useRef, useCallback, useEffect } from 'react';
import { VERTICAL_GAP } from '../layout/constants';
import { findDisconnectedComponents } from '../layout';
import { buildDependencyGraph } from '../layout/graphBuilder';

// Minimum vertical gap between nodes (same as used in Tidy)
const MIN_VERTICAL_GAP = VERTICAL_GAP;

// Debounce time for dimension changes (ms)
const DEBOUNCE_MS = 100;

// Default node dimensions if not measured
const DEFAULT_NODE_WIDTH = 290;
const DEFAULT_NODE_HEIGHT = 150;

/**
 * Check if two rectangles overlap horizontally
 * Returns true if their X ranges intersect
 */
function overlapsHorizontally(rect1, rect2) {
  // Add a small tolerance to catch near-overlaps
  const tolerance = 1;
  return rect1.left < rect2.right + tolerance && rect1.right > rect2.left - tolerance;
}

/**
 * Get the rectangle bounds for a node
 */
function getNodeRect(taskId, positions, nodeDimensions, adjustments = {}) {
  const pos = positions[taskId];
  if (!pos) return null;
  
  const dims = nodeDimensions[taskId] || { width: DEFAULT_NODE_WIDTH, height: DEFAULT_NODE_HEIGHT };
  const adjustment = adjustments[taskId] || 0;
  
  return {
    id: taskId,
    left: pos.x,
    right: pos.x + dims.width,
    top: pos.y + adjustment,
    bottom: pos.y + adjustment + dims.height,
    width: dims.width,
    height: dims.height,
  };
}

/**
 * Calculate vertical adjustments to fix overlaps (push down only).
 * Only considers ACTUAL geometric overlaps (not just Y ordering).
 *
 * Returns a map of taskId -> deltaY (positive = push down).
 */
function calculateVerticalAdjustments(tasks, positions, nodeDimensions) {
  if (!tasks.length || !positions || Object.keys(positions).length === 0) {
    return {};
  }
  
  // Build dependency graph
  const graph = buildDependencyGraph(tasks);
  const components = findDisconnectedComponents(tasks, graph);
  
  const adjustments = {};
  
  // Process each chain independently
  components.forEach(component => {
    const chainTasks = component.tasks;
    
    // Build node info with positions and dimensions
    const nodeInfos = chainTasks
      .map(task => {
        const rect = getNodeRect(task.id, positions, nodeDimensions);
        if (!rect) return null;
        return { task, rect };
      })
      .filter(Boolean);
    
    if (nodeInfos.length < 2) return;
    
    // Sort by Y position (top to bottom), then by X for determinism
    nodeInfos.sort((a, b) => {
      const yDiff = a.rect.top - b.rect.top;
      if (Math.abs(yDiff) > 1) return yDiff;
      return a.rect.left - b.rect.left;
    });
    
    // PHASE 1: Push down to fix overlaps (top to bottom)
    // For each node, check against ALL nodes above it for overlaps
    for (let i = 1; i < nodeInfos.length; i++) {
      const currInfo = nodeInfos[i];
      const currTaskId = currInfo.task.id;
      
      // Get current rect with any accumulated adjustment
      const currRect = getNodeRect(currTaskId, positions, nodeDimensions, adjustments);
      if (!currRect) continue;
      
      // Find the maximum push needed based on all overlapping nodes above
      let maxPushNeeded = 0;
      
      // Check against all nodes that have been processed (above this one)
      for (let j = 0; j < i; j++) {
        const aboveInfo = nodeInfos[j];
        const aboveTaskId = aboveInfo.task.id;
        
        // Get the above node's rect with its adjustment
        const aboveRect = getNodeRect(aboveTaskId, positions, nodeDimensions, adjustments);
        if (!aboveRect) continue;
        
        // Check if they overlap horizontally
        if (!overlapsHorizontally(currRect, aboveRect)) {
          // No horizontal overlap - these nodes are side by side, skip
          continue;
        }
        
        // They DO overlap horizontally - check vertical spacing
        const verticalGap = currRect.top - aboveRect.bottom;
        
        if (verticalGap < MIN_VERTICAL_GAP) {
          // Need to push down - calculate how much
          const pushNeeded = MIN_VERTICAL_GAP - verticalGap;
          maxPushNeeded = Math.max(maxPushNeeded, pushNeeded);
        }
      }
      
      // Apply the push if needed
      if (maxPushNeeded > 0) {
        // Get current adjustment (might already have some from previous iterations)
        const currentAdjustment = adjustments[currTaskId] || 0;
        adjustments[currTaskId] = currentAdjustment + maxPushNeeded;
        
        // IMPORTANT: Also push down all nodes below this one by the same amount
        // This maintains the relative positioning within the chain
        for (let k = i + 1; k < nodeInfos.length; k++) {
          const belowTaskId = nodeInfos[k].task.id;
          const belowAdjustment = adjustments[belowTaskId] || 0;
          adjustments[belowTaskId] = belowAdjustment + maxPushNeeded;
        }
      }
    }
  });

  return adjustments;
}

/**
 * Hook to automatically adjust vertical spacing when node dimensions change
 */
export function useVerticalSpacingOnResize({
  tasks,
  nodePositions,
  nodeDimensions,
  setCustomPositions,
  onPositionChangeBatch,
  onAnimationStart,
  onAnimationEnd,
  isMultiCampaignMode = false,
  /** When false, skip (e.g. settled single-campaign ELK uses debounced full relayout). */
  enabled = true,
}) {
  // Refs to hold current values for debounced callback
  const tasksRef = useRef(tasks);
  const nodePositionsRef = useRef(nodePositions);
  const nodeDimensionsRef = useRef(nodeDimensions);
  const setCustomPositionsRef = useRef(setCustomPositions);
  const onPositionChangeBatchRef = useRef(onPositionChangeBatch);
  const onAnimationStartRef = useRef(onAnimationStart);
  const onAnimationEndRef = useRef(onAnimationEnd);
  const isMultiCampaignModeRef = useRef(isMultiCampaignMode);

  // Track previous dimensions to detect changes
  const prevDimensionsSignatureRef = useRef('');
  
  // Debounce timer
  const debounceTimerRef = useRef(null);
  
  // Animation timer
  const animationTimerRef = useRef(null);
  
  // Update refs
  useEffect(() => {
    tasksRef.current = tasks;
    nodePositionsRef.current = nodePositions;
    nodeDimensionsRef.current = nodeDimensions;
    setCustomPositionsRef.current = setCustomPositions;
    onPositionChangeBatchRef.current = onPositionChangeBatch;
    onAnimationStartRef.current = onAnimationStart;
    onAnimationEndRef.current = onAnimationEnd;
    isMultiCampaignModeRef.current = isMultiCampaignMode;
  });
  
  // Create a signature for current dimensions (height-focused)
  const getDimensionsSignature = useCallback((dims) => {
    return Object.entries(dims)
      .map(([id, d]) => `${id}:${Math.round(d.height)}`)
      .sort()
      .join('|');
  }, []);
  
  // Apply vertical spacing adjustments
  const applyVerticalSpacing = useCallback(() => {
    const currentTasks = tasksRef.current;
    const positions = nodePositionsRef.current;
    const dimensions = nodeDimensionsRef.current;
    const setPositions = setCustomPositionsRef.current;
    const onBatchChange = onPositionChangeBatchRef.current;
    const onAnimStart = onAnimationStartRef.current;
    const onAnimEnd = onAnimationEndRef.current;
    
    if (!currentTasks.length || !positions || Object.keys(positions).length === 0) {
      return;
    }
    
    // Calculate needed adjustments
    const adjustments = calculateVerticalAdjustments(currentTasks, positions, dimensions);
    
    // Check if any adjustments are needed
    const hasAdjustments = Object.keys(adjustments).length > 0;
    
    if (!hasAdjustments) {
      return;
    }
    
    // Build new positions with adjustments applied
    const newPositions = { ...positions };
    const positionChanges = [];
    
    Object.entries(adjustments).forEach(([taskId, deltaY]) => {
      // Skip if adjustment is negligible (less than 1px)
      if (Math.abs(deltaY) >= 1) {
        const oldPos = positions[taskId];
        if (!oldPos) return;
        
        const newPos = {
          x: oldPos.x,
          y: Math.round(oldPos.y + deltaY),
        };
        newPositions[taskId] = newPos;
        
        const task = currentTasks.find(t => t.id === taskId);
        if (task) {
          positionChanges.push({
            taskId,
            position: newPos,
            oldPosition: oldPos,
          });
        }
      }
    });
    
    if (positionChanges.length === 0) {
      return;
    }
    
    // Trigger animation
    onAnimStart?.();
    
    // Update visual positions
    if (setPositions) {
      setPositions(newPositions);
    }
    
    // Save positions to database (with skipRecordAction to avoid undo clutter)
    if (onBatchChange && positionChanges.length > 0) {
      onBatchChange(positionChanges, { 
        batchType: 'VERTICAL_SPACING', 
        batchDescription: 'Auto-adjust spacing',
        skipRecordAction: true,
      });
    }
    
    // End animation after transitions complete
    if (animationTimerRef.current) {
      clearTimeout(animationTimerRef.current);
    }
    animationTimerRef.current = setTimeout(() => {
      onAnimEnd?.();
    }, 350); // Match CSS transition duration
    
  }, []);
  
  // Watch for dimension changes
  useEffect(() => {
    if (!enabled) return undefined;
    const currentSignature = getDimensionsSignature(nodeDimensions);
    const prevSignature = prevDimensionsSignatureRef.current;
    
    // Skip first render
    if (prevSignature === '') {
      prevDimensionsSignatureRef.current = currentSignature;
      return;
    }
    
    // Check if heights changed
    if (currentSignature !== prevSignature) {
      prevDimensionsSignatureRef.current = currentSignature;
      
      // Debounce the spacing adjustment
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      
      debounceTimerRef.current = setTimeout(() => {
        applyVerticalSpacing();
      }, DEBOUNCE_MS);
    }
  }, [nodeDimensions, getDimensionsSignature, applyVerticalSpacing, enabled]);

  useEffect(() => {
    if (enabled) return;
    prevDimensionsSignatureRef.current = '';
  }, [enabled]);
  
  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (animationTimerRef.current) {
        clearTimeout(animationTimerRef.current);
      }
    };
  }, []);
  
  // Return a manual trigger function for testing/debugging
  return {
    applyVerticalSpacing,
  };
}

export default useVerticalSpacingOnResize;
